import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '../login.service';
import { FlashMessagesService } from 'angular2-flash-messages';

@Component({
  selector: 'app-meta-template-approval',
  templateUrl: './meta-template-approval.component.html',
  styleUrls: ['./meta-template-approval.component.css']
})
export class MetaTemplateApprovalComponent implements OnInit {
  currentUser: any = {};
  showLoader: boolean = false;
  showSampleModal: boolean = false;

  template: any = {
    name: '',
    category: '',
    type: 'text',
    language: '',
    headerType: 'none',
    headerText: '',
    mediaType: 'IMAGE',
    body: '',
    footer: '',
    buttonType: '',
    quickReplyButtons: [],
    callToActionButtons: []
  };

  sampleData: any = {
    headerFile: null,
    headerPreviewUrl: null,
    bodyVariables: [],
    buttonVariables: [],
    isSaved: false
  };

  constructor(
    private router: Router,
    private loginService: LoginService,
    private flashMessagesService: FlashMessagesService
  ) { }

  ngOnInit(): void {
    this.currentUser = JSON.parse(this.loginService.getLocalSession('currentUser'));
  }

  getLanguageName(code: string): string {
    const languages = {
      'en': 'English',
      'en_GB': 'English (UK)',
      'en_US': 'English (US)',
      'ta': 'Tamil'
    };
    return languages[code] || code;
  }

  getCurrentTime(): string {
    const now = new Date();
    return now.getHours().toString().padStart(2, '0') + ':' +
      now.getMinutes().toString().padStart(2, '0');
  }

  getTemplateNameLength(): number {
    return this.template.name ? this.template.name.length : 0;
  }

  getHeaderTextLength(): number {
    return this.template.headerText ? this.template.headerText.length : 0;
  }

  getBodyLength(): number {
    return this.template.body ? this.template.body.length : 0;
  }

  getFooterLength(): number {
    return this.template.footer ? this.template.footer.length : 0;
  }

  addQuickReplyButton(): void {
    if (this.template.quickReplyButtons.length < 3) {
      this.template.quickReplyButtons.push({ text: '' });
    }
  }

  removeQuickReplyButton(index: number): void {
    this.template.quickReplyButtons.splice(index, 1);
  }

  addCallToActionButton(): void {
    if (this.template.callToActionButtons.length < 2) {
      this.template.callToActionButtons.push({
        type: 'PHONE_NUMBER',
        text: '',
        phoneNumber: '',
        countryCode: '+91',
        country: 'India',
        url: '',
        urlType: 'static',
        offerCode: ''
      });
    }
  }

  removeCallToActionButton(index: number): void {
    this.template.callToActionButtons.splice(index, 1);
  }

  openSampleModal(): void {
    this.extractVariables();
    this.showSampleModal = true;
  }

  closeSampleModal(): void {
    this.showSampleModal = false;
  }

  extractVariables(): void {
    // Extract variables from body text
    const bodyMatches = this.template.body.match(/{{\d+}}/g) || [];
    this.sampleData.bodyVariables = bodyMatches.map((match, index) => ({
      placeholder: match,
      value: ''
    }));

    // Extract variables from button URLs
    this.sampleData.buttonVariables = [];
    if (this.template.buttonType === 'CALL_TO_ACTION') {
      this.template.callToActionButtons.forEach((button, btnIndex) => {
        if (button.url) {
          const urlMatches = button.url.match(/{{\d+}}/g) || [];
          urlMatches.forEach(match => {
            this.sampleData.buttonVariables.push({
              placeholder: match,
              value: '',
              buttonText: button.text,
              buttonIndex: btnIndex
            });
          });
        }
      });
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.sampleData.headerFile = file;
      // Create preview URL for the file
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.sampleData.headerPreviewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  saveSampleData(): void {
    this.sampleData.isSaved = true;
    this.closeSampleModal();
  }

  validateTemplateName(name: string): boolean {
    // Template name should only contain lowercase letters, numbers, and underscores
    const pattern = /^[a-z0-9_]+$/;
    return pattern.test(name) && name.length > 0;
  }

  hasVariableAtStartOrEnd(text: string): boolean {
    if (!text) return false;
    const trimmed = text.trim();
    return /^{{\d+}}/.test(trimmed) || /{{\d+}}$/.test(trimmed);
  }

  getVariablePositionWarning(): string {
    if (this.hasVariableAtStartOrEnd(this.template.body)) {
      return 'The body text contains variable parameters at the beginning or end. Check to make sure this is the correct format.';
    }
    return '';
  }

  submitTemplate(): void {
    if (!this.validateTemplateName(this.template.name)) {
      this.flashMessagesService.show('Template name must contain only lowercase letters, numbers, and underscores',
        { cssClass: 'alert-danger' });
      return;
    }

    const hasBodyVariables = this.template.body && /{{\d+}}/.test(this.template.body);
    const hasMediaHeader = this.template.headerType === 'media';

    if ((hasBodyVariables || hasMediaHeader) && !this.sampleData.isSaved) {
      this.flashMessagesService.show(
        hasMediaHeader && hasBodyVariables
          ? 'Please add sample media and variable values before submitting'
          : hasMediaHeader
            ? 'Please add a sample media file before submitting'
            : 'Please add sample values for variables before submitting',
        { cssClass: 'alert-danger' }
      );
      return;
    }

    this.showLoader = true;

    const templateData = {
      name: this.template.name,
      category: this.template.category,
      language: this.template.language,
      components: this.buildComponents(),
      headerBase64: (hasMediaHeader && this.sampleData.headerPreviewUrl) ? this.sampleData.headerPreviewUrl : null,
      headerMediaType: hasMediaHeader ? this.template.mediaType : null,
      bodySamples: this.sampleData.bodyVariables ? this.sampleData.bodyVariables.map((v: any) => v.value || 'sample') : [],
      buttonSamples: this.sampleData.buttonVariables ? this.sampleData.buttonVariables.map((v: any) => ({ buttonText: v.buttonText, value: v.value || 'sample' })) : []
    };

    this.loginService.submitMetaTemplate(templateData, this.currentUser).subscribe(
      (response: any) => {
        this.showLoader = false;
        if (response.status === 'success') {
          const statusMsg = response.approval_status ? ` Status: ${response.approval_status}` : '';
          this.flashMessagesService.show(`Template submitted successfully!${statusMsg}`, { cssClass: 'alert-success' });
          alert(`Template submitted successfully!${statusMsg}`)
          this.router.navigate(['/WhatsAppMsgTemplate']);
        } else {
          this.flashMessagesService.show(response.message || 'Failed to submit template', { cssClass: 'alert-danger' });
          alert('Failed to submit template: ' + (response.message || 'Unknown error'));
        }
      },
      (error) => {
        this.showLoader = false;
        this.flashMessagesService.show('Network error occurred', { cssClass: 'alert-danger' });
      }
    );
  }

  buildComponents(): any[] {
    const components = [];

    // Header component
    if (this.template.headerType === 'text') {
      components.push({
        type: 'HEADER',
        format: 'TEXT',
        text: this.template.headerText
      });
    } else if (this.template.headerType === 'media') {
      components.push({
        type: 'HEADER',
        format: this.template.mediaType
      });
    }

    // Body component
    const bodyComponent: any = {
      type: 'BODY',
      text: this.template.body
    };
    const bodyVarMatches = this.template.body ? this.template.body.match(/{{\d+}}/g) : null;
    if (bodyVarMatches && this.sampleData.bodyVariables.length > 0) {
      const samples = this.sampleData.bodyVariables.map(v => v.value || 'sample');
      bodyComponent.example = { body_text: [samples] };
    }
    components.push(bodyComponent);

    // Footer component
    if (this.template.footer) {
      components.push({
        type: 'FOOTER',
        text: this.template.footer
      });
    }

    // Buttons component
    if (this.template.buttonType === 'QUICK_REPLY' && this.template.quickReplyButtons.length > 0) {
      const buttons = this.template.quickReplyButtons
        .filter(btn => btn.text.trim())
        .map(btn => ({
          type: 'QUICK_REPLY',
          text: btn.text
        }));

      if (buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: buttons
        });
      }
    } else if (this.template.buttonType === 'CALL_TO_ACTION' && this.template.callToActionButtons.length > 0) {
      const buttons = this.template.callToActionButtons
        .filter(btn => btn.text.trim())
        .map(btn => {
          if (btn.type === 'PHONE_NUMBER') {
            return {
              type: 'PHONE_NUMBER',
              text: btn.text,
              phone_number: btn.countryCode + btn.phoneNumber
            };
          } else if (btn.type === 'URL') {
            const urlBtn: any = {
              type: 'URL',
              text: btn.text,
              url: btn.url
            };
            const urlVarMatch = btn.url ? btn.url.match(/{{\d+}}/g) : null;
            if (urlVarMatch) {
              const btnSample = this.sampleData.buttonVariables.find((v: any) => v.buttonText === btn.text);
              urlBtn.example = [btnSample && btnSample.value ? btnSample.value : 'sample'];
            }
            return urlBtn;
          } else if (btn.type === 'COPY_CODE') {
            return {
              type: 'COPY_CODE',
              text: btn.text,
              example: [btn.offerCode]
            };
          }
          return null;
        })
        .filter(btn => btn !== null);

      if (buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: buttons
        });
      }
    }

    return components;
  }

  cancel(): void {
    this.router.navigate(['/WhatsAppMsgTemplate']);
  }

  onTemplateNameInput(event: any): void {
    let value = event.target.value;
    // Remove spaces and convert to lowercase, keep only valid characters
    value = value.toLowerCase().replace(/ /g, '_').replace(/[^a-z0-9_]/g, '');
    this.template.name = value;
    event.target.value = value;
  }

  getNextVariableNumber(): number {
    const allText = (this.template.body || '') +
      this.template.callToActionButtons.map(btn => btn.url || '').join('');
    const matches = allText.match(/{{\d+}}/g) || [];
    if (matches.length === 0) return 1;

    const numbers = matches.map(match => parseInt(match.replace(/[{}]/g, '')));
    return Math.max(...numbers) + 1;
  }

  insertVariableAtPosition(field: string, buttonIndex?: number): void {
    const variable = `{{${this.getNextVariableNumber()}}}`;

    if (field === 'body') {
      this.template.body = (this.template.body || '') + variable;
    } else if (field === 'buttonUrl' && buttonIndex !== undefined) {
      this.template.callToActionButtons[buttonIndex].url =
        (this.template.callToActionButtons[buttonIndex].url || '') + variable;
    }
  }

  getPreviewBodyText(): string {
    let bodyText = this.template.body || '';
    if (this.sampleData.isSaved && this.sampleData.bodyVariables && this.sampleData.bodyVariables.length > 0) {
      this.sampleData.bodyVariables.forEach(variable => {
        if (variable.value && variable.value.trim()) {
          bodyText = bodyText.replace(new RegExp(variable.placeholder.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
        }
      });
    }
    return bodyText;
  }

  getPreviewButtonUrl(button: any): string {
    let url = button.url || '';
    if (this.sampleData.isSaved && this.sampleData.buttonVariables && this.sampleData.buttonVariables.length > 0) {
      this.sampleData.buttonVariables.forEach(variable => {
        if (variable.value && variable.value.trim() && variable.buttonText === button.text) {
          url = url.replace(new RegExp(variable.placeholder.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
        }
      });
    }
    return url;
  }
}


// WEBPACK FOOTER //
// ./src/app/meta-template-approval/meta-template-approval.component.ts